import { useEffect, useCallback, useState } from "react";
import { useLocation, Navigate } from "react-router";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";

import { queryString, isValidLatitude, isValidLongitude, formatZip } from "capitalrx-utilities";
import { Stack } from "capitalrx-components-2.0/lib/layout";
import { useAlert, Link } from "capitalrx-components-2.0/lib/general";

import { DEFAULT_MAP_COORDINATES } from "@/common/constants/mapConstants";
import { ListLayout } from "@/basic/layouts";
import { Tag, PharmacyDetailsContainer, getAppleMapsURL, MapCoordinates } from "@/basic/components";

import { useGetPharmacy } from "@/api-hooks";
import { PharmacyInfo } from "@/api/types";

interface PharmacyDetailsType {
  pharmacy: PharmacyInfo;
}

export const PharmacyDetails = () => {
  const { t: translate } = useTranslation();
  const pharmacyInfo: Array<PharmacyDetailsType> = [];
  const location = useLocation();
  const getCurrentQueryStrings = queryString.convertQueryStringToJson(location.search);

  const showAlert = useAlert();

  const { data: pharmacy, isLoading, isError } = useGetPharmacy(
    {
      provider_id: getCurrentQueryStrings?.provider_id?.toString(),
    },
    {
      enabled: !!getCurrentQueryStrings?.provider_id,
    },
  );

  const [coordinates, setCoordinates] = useState<MapCoordinates>({ latitude: null, longitude: null });
  const [pharmacyData, setPharmacyData] = useState<Array<PharmacyDetailsType>>();

  useEffect(() => {
    async function fetchData() {
      pharmacyInfo.push({
        // pharmacy: pharmacy.data, maybe?
        pharmacy: pharmacy,
      });

      setPharmacyData(pharmacyInfo);

      setCoordinates({
        latitude: Number(getCurrentQueryStrings.lat),
        longitude: Number(getCurrentQueryStrings.lng),
      });
    }

    fetchData();

  }, [location]);

  const failureHandler = useCallback(() => {
    showAlert({ type: "error", style: "toast", message: translate("PHARMACY_DETAILS_CONTAINER.anErrorOccurred") });
  }, [showAlert]);

  if (isError) {
    failureHandler();
  }

  const center = {
    lat: DEFAULT_MAP_COORDINATES.lat,
    lng: DEFAULT_MAP_COORDINATES.lng,
  };

  if (isValidLatitude(coordinates.latitude) && isValidLongitude(coordinates.longitude)) {
    center.lat = coordinates.latitude;
    center.lng = coordinates.longitude;
  }

  const address = `${pharmacy?.address_line_1}, ${pharmacy?.address_line_2 && `${pharmacy?.address_line_2} , `}${pharmacy?.city}, ${pharmacy?.state}, ${formatZip(pharmacy?.zip)}`;
  const tags: Array<string> = [];
  const url = getAppleMapsURL({ data: { address } });

  const pageTitleChildren = (
    <Stack spacing="mini">
      <>
        {tags && tags.map((tagName: string) => {
          <Tag key={tagName}>{tagName}</Tag>;
        })}

        {isLoading ? (
          <Skeleton />
        ) : (
          <Link
            to={url}
          >
            {address}
          </Link>
        )}
      </>
    </Stack>
  );

  const backButtonParams = queryString.convertJsonToQueryString({
    client: getCurrentQueryStrings?.client?.toString(),
    zip_code: getCurrentQueryStrings?.zip_code?.toString(),
    coverage: getCurrentQueryStrings?.coverage?.toString(),
    lat: getCurrentQueryStrings?.lat?.toString(),
    lng: getCurrentQueryStrings?.lng?.toString(),
  });

  const isParamsSet = getCurrentQueryStrings?.zip_code
    && getCurrentQueryStrings?.client
    && getCurrentQueryStrings?.lat
    && getCurrentQueryStrings?.lng
    && getCurrentQueryStrings?.coverage
    && getCurrentQueryStrings?.provider_id;

  if (!isParamsSet) {
    const client = getCurrentQueryStrings?.client?.toString();
    const notFoundParams = client ? queryString.convertJsonToQueryString({ client }) : undefined;

    return (
      <Navigate
        to={{
          pathname: "/404",
          search: notFoundParams,
        }}
      />
    );
  }

  return (
    <ListLayout
      pageTitle={isLoading ? <Skeleton /> : pharmacy?.name}
      pageTitleChildren={pageTitleChildren}
      backButtonParams={backButtonParams}
      backButtonPath="/pharmacy/list"
    >
      <PharmacyDetailsContainer pharmacyData={pharmacyData} data={pharmacy} isLoading={isLoading} center={center} />
    </ListLayout>
  );
};
